import {useState} from 'react'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {
    Box,
    ChevronRight,
    FolderTree,
    GitBranch,
    KeyRound,
    Shield,
    Tag,
    Trash2,
} from 'lucide-react'
import {useNavigate, useParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {Button} from '@/components/ui/button'
import {DeleteConfirmDialog} from '@/components/shared/DeleteConfirmDialog'
import {Icon} from '@/components/shared/Icon'
import {CodeText} from '@/components/shared/CodeText'
import {routes} from '@/lib/routes'
import {permissions} from '@/lib/permissions'
import {useAuth} from '@/contexts/auth-context'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {RiskLevelBadge} from '@/components/ui/risk-level-badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {ResourceResponseFields} from '@/types/resource'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {formatTimestamp} from '@/lib/date-utils'
import {InlineAttributeEditor} from '@/components/attributes/InlineAttributeEditor'
import {OwnersSection, OwnersEntityType} from '@/components/owners/OwnersSection'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {PageLayout} from '@/components/shared/PageLayout'
import {CardSection} from '@/components/shared/CardSection'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {EntityLink} from '@/components/shared/EntityLink'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {Strong} from '@/components/shared/Strong'
import {resolveTypeValueIcon} from '@/components/entity-type/entity-type-icon'


export function ResourceDetail() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.resources.detailPageTitle)
    const {id} = useParams<{ id: string }>()
    const navigate = useNavigate()
    const {hasPermission} = useAuth()
    const canUpdate = hasPermission(permissions.resources.update)
    const canDelete = hasPermission(permissions.resources.delete)
    const [deleteOpen, setDeleteOpen] = useState(false)
    const queryClient = useQueryClient()

    const deleteMutation = useEntityMutation({
        mutationFn: () => services.resources.remove!(id!),
        successMessage: i18n.resources.deleteSuccess,
        onSuccess: () => {
            setDeleteOpen(false)
            queryClient.invalidateQueries({queryKey: ['resources']})
            navigate(routes.systems.resources.list())
        },
    })

    // Fetch resource
    const {data: resource, isLoading, error} = useQuery({
        queryKey: [ResourceResponseFields.id, id],
        queryFn: () => services.resources.detail(id!),
        enabled: !!id,
    })

    // Fetch enforcement objects for this resource
    const {data: enforcementObjectsData} = useQuery({
        queryKey: ['enforcement-objects', 'by-resource', id],
        queryFn: () => services.enforcementObjects.byResource(id!),
        enabled: !!id,
    })
    const enforcementObjects = enforcementObjectsData || []

    // Fetch ancestors (for breadcrumb)
    const {data: ancestors = []} = useQuery({
        queryKey: ['resource-ancestors', id],
        queryFn: () => services.resources.ancestors(id!),
        enabled: !!id,
    })

    // Fetch children
    const {data: children = []} = useQuery({
        queryKey: ['resource-children', id],
        queryFn: () => services.resources.children(id!),
        enabled: !!id && !!resource?.hasChildren,
    })


    const INHERITED_KEY = 'inherited-profiles' as const

    // Fetch inherited access profiles
    const {data: inheritedProfiles = []} = useQuery({
        queryKey: ['access-profiles', INHERITED_KEY, id],
        queryFn: () => services.accessProfiles.byResourceInherited(id!),
        enabled: !!id,
    })

    const {resourceTypes, dataClassificationTypes, enforcementTypes, resourceResponsibilityTypes} = useAppConfig()

    const getClassificationColor = (classification: string | null) => {
        if (!classification) return BadgeVariant.OUTLINE
         
        switch (classification.toUpperCase()) {
            case 'PUBLIC':
                return BadgeVariant.SUCCESS
            case 'INTERNAL':
                return BadgeVariant.SECONDARY
            case 'CONFIDENTIAL':
                return BadgeVariant.WARNING
            case 'RESTRICTED':
                return BadgeVariant.DESTRUCTIVE
            default:
                return BadgeVariant.OUTLINE
        }
         
    }

    // Build ancestor breadcrumb (ancestors returned from immediate parent to root, reverse for display)
    const breadcrumbAncestors = [...ancestors].reverse()

    const r = resource

    const HeaderIcon = resolveTypeValueIcon(resourceTypes, resource?.resourceType, Box)

    return (
        <PageLayout
            data-testid="resource-detail-page"
            variant="detail"
            title={resource?.derivedDisplayName ?? ''}
            icon={HeaderIcon}
            backTo={routes.systems.resources.list()}
            loading={isLoading}
            error={error || (!isLoading && !resource ? new Error('Resource not found') : undefined)}
            actions={resource ? (
                <>
                    <Badge variant="outline">
                        {labelOf(resourceTypes, resource.resourceType)}
                    </Badge>
                    <Badge variant={getClassificationColor(resource.dataClassification)}>
                        {labelOf(dataClassificationTypes, resource.dataClassification || '')}
                    </Badge>
                    {canDelete && (
                        <Button
                            data-testid="btn-delete-resource"
                            size="sm"
                            variant="destructive"
                            onClick={() => setDeleteOpen(true)}
                        >
                            <Icon icon={Trash2} className="mr-1"/>
                            {i18n.resources.deleteResource}
                        </Button>
                    )}
                </>
            ) : undefined}
        >
            {r && (<>
                <DeleteConfirmDialog
                    open={deleteOpen}
                    onOpenChange={setDeleteOpen}
                    title={i18n.resources.deleteConfirmTitle}
                    description={i18n.resources.deleteConfirmDescription({name: r.derivedDisplayName})}
                    testIdPrefix="delete-resource"
                    isPending={deleteMutation.isPending}
                    onConfirm={() => deleteMutation.mutate(undefined)}
                />
                {breadcrumbAncestors.length > 0 && (
                    <InlineRow gap="xs" wrap className="text-sm">
                        {breadcrumbAncestors.map((ancestor, idx) => (
                            <InlineRow key={ancestor.resource.id} gap="xs">
                                {idx > 0 && <ChevronRight className="h-3 w-3"/>}
                                <TextLink
                                    testId={`link-ancestor-${ancestor.resource.id}`}
                                    to={routes.systems.resources.detail(ancestor.resource.id)}
                                >
                                    {ancestor.resource.displayName}
                                </TextLink>
                            </InlineRow>
                        ))}
                        <ChevronRight className="h-3 w-3"/>
                        <Strong>{r.derivedDisplayName}</Strong>
                    </InlineRow>
                )}
                <DetailsCard
                    data-testid="card-resource-details"
                    title={i18n.resources.resourceDetails}
                    description={i18n.resources.configAndMetadata}
                    fields={[
                        {label: i18n.resources.nativeKey, value: <CodeText>{r.nativeKey}</CodeText>},
                        {label: i18n.resources.resourceType, value: labelOf(resourceTypes, r.resourceType)},
                        {label: i18n.resources.dataClassification, value: labelOf(dataClassificationTypes, r.dataClassification || '')},
                        {
                            label: i18n.systems.title,
                            value: <TextLink testId="link-system" to={routes.systems.detail(r.targetSystem.id)}>{r.targetSystem.displayName}</TextLink>,
                        },
                        ...(r.parent ? [{
                            label: i18n.resources.parentResource,
                            value: <TextLink testId="link-parent-resource" to={routes.systems.resources.detail(r.parent.id)}>{r.parent.displayName}</TextLink>,
                        }] : []),
                        {label: i18n.common.createdAt, value: formatTimestamp(r.createdAt)},
                        {label: i18n.common.lastUpdated, value: formatTimestamp(r.updatedAt)},
                    ]}
                />

                {/* Attributes */}
                <CardSection data-testid="section-attributes" icon={Tag} title={i18n.common.attributes}>
                    <InlineAttributeEditor
                        entityType={AttributeEntityType.RESOURCE}
                        entityId={id!}
                        entityTypeCode={r.resourceType}
                        canEdit={canUpdate}
                    />
                </CardSection>

                {/* Children */}
                {r.hasChildren && children.length > 0 && (
                    <CardSection data-testid="section-children" icon={FolderTree} title={i18n.resources.children} description={i18n.resources.childCount({count: children.length})}>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-child-name">{i18n.common.name}</TableHead>
                                    <TableHead data-testid="col-child-resource-type">{i18n.resources.resourceType}</TableHead>
                                    <TableHead data-testid="col-child-data-classification">{i18n.resources.dataClassification}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {children.map((child) => (
                                    <ClickableTableRow
                                        key={child.id}
                                        to={routes.systems.resources.detail(child.id)}
                                    >
                                        <TableCell>
                                            <TextLink
                                                testId={`link-child-resource-${child.id}`}
                                                to={routes.systems.resources.detail(child.id)}
                                                stopPropagation
                                                variant="bold"
                                            >
                                                {child.derivedDisplayName}
                                            </TextLink>
                                            <MutedText size="sm">{child.nativeKey}</MutedText>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">
                                                {labelOf(resourceTypes, child.resourceType)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={getClassificationColor(child.dataClassification)}>
                                                {labelOf(dataClassificationTypes, child.dataClassification || '')}
                                            </Badge>
                                        </TableCell>
                                    </ClickableTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardSection>
                )}

                <OwnersSection
                    entityType={OwnersEntityType.resources}
                    entityId={id!}
                    canEdit={canUpdate}
                    responsibilityOptions={resourceResponsibilityTypes}
                />

                {/* Enforcement Objects */}
                <CardSection data-testid="section-enforcement-objects" icon={Shield} title={i18n.enforcementObjects.title} description={i18n.resources.enforcementObjectCount({count: enforcementObjects.length})}>
                    {enforcementObjects.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-eo-name">{i18n.common.name}</TableHead>
                                    <TableHead data-testid="col-eo-type">{i18n.common.type}</TableHead>
                                    <TableHead data-testid="col-eo-system">{i18n.resources.system}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {enforcementObjects.map((eo) => (
                                    <ClickableTableRow
                                        key={eo.id}
                                        to={routes.systems.enforcementObjects.detail(eo.id)}
                                    >
                                        <TableCell>
                                            <TextLink
                                                testId={`link-enforcement-object-${eo.id}`}
                                                to={routes.systems.enforcementObjects.detail(eo.id)}
                                                stopPropagation
                                                variant="bold"
                                            >
                                                {eo.derivedDisplayName}
                                            </TextLink>
                                            <MutedText size="sm">{eo.externalIdentifier}</MutedText>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">
                                                {labelOf(enforcementTypes, eo.enforcementType)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <EntityLink
                                                value={eo.targetSystem}
                                                stopPropagation
                                                testId={`link-enforcement-object-system-${eo.id}`}
                                            />
                                        </TableCell>
                                    </ClickableTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <MutedText block centered className="py-4">{i18n.resources.noEnforcementObjects}</MutedText>
                    )}
                </CardSection>

                {/* Inherited Access Profiles */}
                {inheritedProfiles.length > 0 && (
                    <CardSection data-testid="section-access-profiles" icon={KeyRound} title={  'Access Profiles'}>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-profile-name">{i18n.common.name}</TableHead>
                                    <TableHead data-testid="col-profile-status">{i18n.common.status}</TableHead>
                                    {/* eslint-disable-next-line i18next/no-literal-string */}
                                    <TableHead data-testid="col-profile-risk">{'Risk'}</TableHead>
                                    {/* eslint-disable-next-line i18next/no-literal-string */}
                                    <TableHead data-testid="col-profile-source">{'Source'}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {inheritedProfiles.map((profile) => (
                                    <ClickableTableRow
                                        key={`${profile.id}-${profile.inherited}`}
                                        to={routes.accessGovernance.profiles.detail(profile.id)}
                                    >
                                        <TableCell>
                                            <TextLink
                                                testId={`link-access-profile-${profile.id}`}
                                                to={routes.accessGovernance.profiles.detail(profile.id)}
                                                stopPropagation
                                                variant="bold"
                                            >
                                                {profile.name}
                                            </TextLink>
                                            {profile.description && (
                                                <MutedText size="sm">{profile.description}</MutedText>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{profile.status}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <RiskLevelBadge riskLevel={profile.riskLevel}/>
                                        </TableCell>
                                        <TableCell>
                                            {profile.inherited && profile.sourceResource ? (
                                                <InlineRow gap="xs">
                                                    <Icon icon={GitBranch} size="xs" muted/>
                                                    <EntityLink
                                                        testId={`link-source-resource-${profile.id}`}
                                                        value={profile.sourceResource}
                                                        stopPropagation
                                                    />
                                                </InlineRow>
                                            ) : (
                                                <MutedText size="sm">
                                                    {/* eslint-disable-next-line i18next/no-literal-string */}
                                                    {'Direct'}
                                                </MutedText>
                                            )}
                                        </TableCell>
                                    </ClickableTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardSection>
                )}
            </>)}
        </PageLayout>
    )
}
